import { Slot } from '@radix-ui/react-slot';
import type { TooltipContentProps } from '@radix-ui/react-tooltip';
import { forwardRef, memo, type ReactNode } from 'react';
import * as Tooltip from '~/components/ui/Tooltip';
import { classNames } from '~/utils/classNames';

export type ButtonVariant = 'primary' | 'secondary' | 'tint' | 'ghost' | 'high-contrast' | 'link-gray' | 'link-color';
export type ButtonSize = 'lg' | 'md' | 'sm';
export type ButtonColor = 'brand' | 'success' | 'warning' | 'danger';

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  asChild?: true;
  truncate?: boolean;
  type?: 'button' | 'submit' | 'reset';
  startIcon?: string;
  endIcon?: string;
  color?: ButtonColor;
  title?: string;
  titleSide?: TooltipContentProps['side'];
  titleAlign?: TooltipContentProps['align'];
  titleSideOffset?: TooltipContentProps['sideOffset'];
  titleMaxWidth?: number;
  loading?: boolean;
  loader?: ReactNode;
}

export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        className,
        variant = 'primary',
        size = 'lg',
        color = 'brand',
        type = 'button',
        disabled = false,
        asChild,
        truncate = true,
        children,
        startIcon,
        endIcon,
        title,
        titleSide = 'bottom',
        titleAlign = 'center',
        titleSideOffset = 6,
        titleMaxWidth,
        loading = false,
        loader,
        ...props
      },
      ref,
    ) => {
      const Comp = asChild ? Slot : 'button';
      const isLinkType = variant === 'link-gray' || variant === 'link-color';

      return (
        <Tooltip.Provider>
          <Tooltip.Root delayDuration={300}>
            <Tooltip.Trigger asChild>
              <Comp
                className={classNames(
                  'flex items-center justify-center font-medium shrink-0 min-w-0 max-w-full rounded-md focus-visible:outline-2 disabled:op-50 relative disabled:cursor-not-allowed',
                  {
                    'gap-2 text-sm': size === 'lg',
                    'gap-1 text-sm': size === 'md',
                    'gap-1 text-xs': size === 'sm',
                    'px-4 h-11': size === 'lg' && !isLinkType,
                    'px-3 h-9': size === 'md' && !isLinkType,
                    'px-2 h-7': size === 'sm' && !isLinkType,
                    'px-0 h-6': size === 'lg' && isLinkType,
                    'px-0 h-5': size === 'md' && isLinkType,
                    'px-0 h-4': size === 'sm' && isLinkType,
                    'focus-visible:outline-bolt-ds-brandHighlight': color === 'brand',
                    'focus-visible:outline-bolt-ds-successHighlight': color === 'success',
                    'focus-visible:outline-bolt-ds-warningHighlight': color === 'warning',
                    'focus-visible:outline-bolt-ds-dangerHighlight': color === 'danger',
                    'text-bolt-ds-onNeutral': variant === 'primary',
                    'bg-bolt-elements-textPrimary text-bolt-elements-background-depth-1': variant === 'high-contrast',
                    'bg-bolt-ds-inverseSurface/10 [&:hover:where(:not(:disabled))]:bg-bolt-ds-inverseSurface/15 text-bolt-ds-textPrimary':
                      variant === 'secondary',
                    'bg-transparent [&:hover:where(:not(:disabled))]:bg-bolt-ds-inverseSurface/7 text-bolt-ds-textPrimary':
                      variant === 'ghost',
                    'bg-transparent text-bolt-ds-textSecondary [&:hover:where(:not(:disabled))]:text-bolt-ds-textPrimary':
                      variant === 'link-gray',
                    'bg-bolt-ds-brand [&:hover:where(:not(:disabled))]:bg-bolt-ds-brandHighlight':
                      variant === 'primary' && color === 'brand',
                    'bg-bolt-ds-success [&:hover:where(:not(:disabled))]:bg-bolt-ds-successHighlight':
                      variant === 'primary' && color === 'success',
                    'bg-bolt-ds-warning [&:hover:where(:not(:disabled))]:bg-bolt-ds-warningHighlight':
                      variant === 'primary' && color === 'warning',
                    'bg-bolt-ds-danger [&:hover:where(:not(:disabled))]:bg-bolt-ds-dangerHighlight':
                      variant === 'primary' && color === 'danger',
                    'bg-bolt-ds-brand/10 [&:hover:where(:not(:disabled))]:bg-bolt-ds-brand/15 text-bolt-ds-brand':
                      variant === 'tint' && color === 'brand',
                    'bg-bolt-ds-success/10 [&:hover:where(:not(:disabled))]:bg-bolt-ds-success/15 text-bolt-ds-success':
                      variant === 'tint' && color === 'success',
                    'bg-bolt-ds-warning/10 [&:hover:where(:not(:disabled))]:bg-bolt-ds-warning/15 text-bolt-ds-warning':
                      variant === 'tint' && color === 'warning',
                    'bg-bolt-ds-danger/10 [&:hover:where(:not(:disabled))]:bg-bolt-ds-danger/15 text-bolt-ds-danger':
                      variant === 'tint' && color === 'danger',
                    'bg-transparent text-bolt-ds-brand [&:hover:where(:not(:disabled))]:text-bolt-ds-brandHighlight':
                      variant === 'link-color' && color === 'brand',
                    'bg-transparent text-bolt-ds-success [&:hover:where(:not(:disabled))]:text-bolt-ds-successHighlight':
                      variant === 'link-color' && color === 'success',
                    'bg-transparent text-bolt-ds-warning [&:hover:where(:not(:disabled))]:text-bolt-ds-warningHighlight':
                      variant === 'link-color' && color === 'warning',
                    'bg-transparent text-bolt-ds-danger [&:hover:where(:not(:disabled))]:text-bolt-ds-dangerHighlight':
                      variant === 'link-color' && color === 'danger',
                  },
                  className,
                )}
                ref={ref}
                disabled={disabled || loading}
                type={type}
                {...props}
                children={
                  asChild ? (
                    children
                  ) : (
                    <ButtonContent
                      loading={loading}
                      loader={loader}
                      size={size}
                      startIcon={startIcon}
                      endIcon={endIcon}
                      truncate={truncate}
                      children={children}
                    />
                  )
                }
              />
            </Tooltip.Trigger>

            {title && (
              <Tooltip.Portal>
                <Tooltip.Content
                  side={titleSide}
                  align={titleAlign}
                  sideOffset={titleSideOffset}
                  maxWidth={titleMaxWidth}
                >
                  {title}
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    },
  ),
);

type ButtonContentProps = Pick<
  ButtonProps,
  'loading' | 'loader' | 'size' | 'startIcon' | 'endIcon' | 'truncate' | 'children'
>;

const ButtonContent = ({ loading, loader, size, startIcon, endIcon, truncate, children }: ButtonContentProps) => {
  return (
    <>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">{loader || <DefaultLoader />}</div>
      )}

      {!truncate ? (
        children
      ) : (
        <>
          {startIcon && <ButtonIcon size={size} icon={startIcon} loading={loading} />}

          <span className={classNames('truncate', loading ? 'op-0' : '')}>{children}</span>

          {endIcon && <ButtonIcon size={size} icon={endIcon} loading={loading} />}
        </>
      )}
    </>
  );
};

interface ButtonIconProps {
  size: ButtonProps['size'];
  icon: string;
  loading?: boolean;
}

const ButtonIcon = ({ size, icon, loading }: ButtonIconProps) => {
  return (
    <span
      className={classNames(icon, 'flex items-center justify-center', {
        'h-5 text-xl leading-5': size === 'lg' || size === 'md',
        'h-4 text-base leading-4': size === 'sm',
        'op-0': loading,
      })}
    />
  );
};

export function DefaultLoader({ className, size }: Pick<ButtonProps, 'className' | 'size'>) {
  return (
    <span
      className={classNames(className, 'i-svg-spinners:90-ring-with-bg', {
        'scale-150': size === 'lg',
        'scale-125': size === 'md',
        'scale-115': size === 'sm',
      })}
    />
  );
}
